/* eslint-disable multiline-ternary */
import React, { useCallback, useEffect, useMemo } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import {
  AddToPlaylist,
  Alert,
  AlertTitle,
  CiteDialog,
  GetMindToolsDialog,
  Link,
  ShareDialogPanel,
  Toast,
  TranslateDialog,
  GoogleAd
} from '..'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useCitation, useGoogleAdsense, usePlaylistNavigation, useAlert } from '../../hooks'
import { campaignSlice, contentSlice, userSlice, toastSlice } from '../../reducers'
import { useAddToPlaylist } from '../add-to-playlist-dialog'
import { VerticalCampaignBanner } from '../campaign/vertical-banner'
import NewsletterCTA from '../newsletter-cta'
import { useStyles } from './styles'
import { CONTENT_QUERY_STRING, STATIC_AD_SLOTS } from '../../consts'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../router/routes'
import PlaylistNavigation from '../playlist-navigation'
import Header from '../article/header'
import clsx from 'clsx'
import { PlaylistProgressProvider } from '../playlist-progress/context'

const PdfDialogPanel = React.lazy(() => import('./../get-mt-dialog/pdf-dialog-panel'))
const Article = React.lazy(() => import('./../article'))

const DownloadPdfFallback = () => (
  <Box display='flex' flex={1} justifyContent='center' alignContent='center' p={4}>
    <CircularProgress />
  </Box>
)

const ScrollToTopOnMount = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return null
}

const ArticleView = () => {
  const modal = useSelector(contentSlice.selectors.selectShowModal)
  const showClipboardToast = useSelector(state =>
    toastSlice.selectors.selectShowToast(state, 'clipboard')
  )
  const showLanguageToast = useSelector(state =>
    toastSlice.selectors.selectShowToast(state, 'set-default-language')
  )
  const profile = useSelector(userSlice.selectors.selectProfile)
  const allLanguages = useSelector(contentSlice.selectors.selectAllLanguages)
  const language = useSelector(contentSlice.selectors.selectLanguage)
  const content = useSelector(contentSlice.selectors.selectContent)
  const campaign = useSelector(campaignSlice.selectors.campaignData)
  const { playlistContextId, playlistBreadcrumbs, playlistDetail, playlistContent } = usePlaylistNavigation()

  const classes = useStyles()
  const navigate = useNavigate()
  const { pathname, origin, search } = window.location
  const url = `${origin}${pathname}`
  const sharableUrl = useMemo(() => {
    const searchParam = new URLSearchParams(search)
    searchParam.set('from', 'shared-link')

    return `${url}?${searchParam.toString()}`
  }, [url, search])

  const citation = useCitation(content, url)
  const dispatch = useDispatch()

  const { enqueueMsg } = useAlert()

  const { showAddToPlaylist, getPlaylistArticle } = useAddToPlaylist()

  const profileLanguage = profile?.defaultLanguage
  const storedLanguage = localStorage.getItem('defaultLanguage')
  const defaultLanguage = profileLanguage || storedLanguage

  const [setDefaultLanguage] = useEvent([{
    eventName: 'setDefaultLanguage',
    onSuccess: (payload) => {
      dispatch(userSlice.actions.setProfile(payload))
      dispatch(toastSlice.actions.showToast('set-default-language'))
    },
    onError: (err) => {
      console.error('Unable to update default language', err)
      enqueueMsg('Unable to update default language', 'error')
    }
  }])

  const handleSetDefaultLanguage = (newDefaultLanguage) => {
    const partial = {
      defaultLanguage: newDefaultLanguage,
      source: 'content-page'
    }
    if (!profileLanguage || profileLanguage !== newDefaultLanguage) {
      setDefaultLanguage.trigger({ partial })
    }
  }

  const handleDialogClose = () => dispatch(contentSlice.actions.showModal())

  const handleToastClose = name => dispatch(toastSlice.actions.hideToast(name))

  const handleClipboardCopy = () =>
    dispatch(toastSlice.actions.showToast('clipboard'))

  const setTranslationLanguageOnUrl = useCallback((language) => {
    const { pathname, search } = window.location
    dispatch(contentSlice.actions.setIsTranslating(true))
    if (language && language !== 'en') {
      const query = (new URLSearchParams(search))
      query.set(CONTENT_QUERY_STRING.TRANSLATION, language)

      navigate(`${pathname}?${query.toString()}`, { replace: true })
    } else {
      const query = new URLSearchParams(search)
      query.delete(CONTENT_QUERY_STRING.TRANSLATION)
      const queryString = query.toString()
      const fullPath = queryString ? `${pathname}?${queryString}` : pathname

      navigate(fullPath, { replace: true })
    }
    if (language && language === 'en') {
      const query = (new URLSearchParams(search))
      query.set(CONTENT_QUERY_STRING.TRANSLATION, language)
      navigate(`${pathname}?${query.toString()}`, { replace: true })
    }
  }, [navigate, dispatch])

  const handleArticleTranslate = lang => {
    if (lang === language.value) {
      console.warn('The selected language is already the current language. No translation needed.')
      return
    }
    setTranslationLanguageOnUrl(lang)
    dispatch(contentSlice.actions.setLanguage(lang))
  }

  const handleAddToPlaylist = article => showAddToPlaylist(article)

  const { useAdsense, shouldDisplayContentStaticAds } = useGoogleAdsense()

  if (window.DD_RUM && typeof window.DD_RUM.addTiming === 'function') {
    window.DD_RUM.addTiming('article_view_loaded')
  } else {
    console.log('addTiming is not available')
  }

  return (
    <>
      <ScrollToTopOnMount />
      {modal === 'cite' && (
        <CiteDialog
          open
          locationId={content.locationId}
          onClose={handleDialogClose}
          citation={citation}
          onCopy={handleClipboardCopy}
        />
      )}
      {modal === 'share' && (
        <GetMindToolsDialog
          open
          onClose={handleDialogClose}
          pane={
            <ShareDialogPanel
              url={sharableUrl}
              locationId={content.locationId}
              onCopy={handleClipboardCopy}
              title={content.title}
              subtitle={content.subtitle.html}
              image={content.image?.imgUrl}
            />
          }
        />
      )}
      {modal === 'pdf' && (
        <GetMindToolsDialog
          open
          onClose={handleDialogClose}
          pane={
            <React.Suspense fallback={<DownloadPdfFallback />}>
              <PdfDialogPanel
                url={`${url}?from=shared-link`}
                locationId={content.locationId}
                onCopy={handleClipboardCopy}
                content={content}
                onClose={handleDialogClose}
              />
            </React.Suspense>
          }
        />
      )}
      {showLanguageToast && (
        <Toast
          open
          onClose={() => handleToastClose('set-default-language')}
        >
          <Alert severity='success'>
            <AlertTitle>Default content language successfully updated and saved to your
              <Link to={routes.profile.path}> Profile</Link>
            </AlertTitle>
          </Alert>
        </Toast>
      )}
      {modal === 'translate' && (
        <TranslateDialog
          open
          heading='Translate the article'
          subheading='Please select one of the options below, or type the language you wish'
          onClose={handleDialogClose}
          language={language}
          allLanguages={allLanguages}
          defaultLanguage={defaultLanguage}
          onTranslate={handleArticleTranslate}
          handleSetDefaultLanguage={handleSetDefaultLanguage}
        />
      )}
      {showClipboardToast && (
        <Toast open onClose={() => handleToastClose('clipboard')}>
          <Alert severity='success'>
            <AlertTitle>Copied to clipboard</AlertTitle>
          </Alert>
        </Toast>
      )}
      <AddToPlaylist article={getPlaylistArticle()} backText='Back to Article' />
      {content && (
        <>
          <div id={content.locationId} className={classes.contentGrid}>
            <Box className={classes.leftPanel} />
            <Box className={clsx(classes.header, playlistContextId && classes.headerPlaylist)}>
              <PlaylistProgressProvider>
                <Header
                  content={content}
                  playlistBreadcrumbs={playlistBreadcrumbs}
                  playlistContextId={playlistContextId}
                  handleAddToPlaylist={handleAddToPlaylist}
                />
              </PlaylistProgressProvider>
            </Box>
            <Box className={classes.content}>
              <Article
                key={content.locationId}
                content={content}
                onAddToPlaylist={handleAddToPlaylist}
                width={668}
                minInset={2}
              />
            </Box>
            <Box
              mt={5}
              ml={2}
              display='flex'
              flexDirection='column'
              alignItems='center'
              className={classes.rightPanel}
            >
              <Box className={classes.playlistNavContainer} mb={2}>
                {playlistContextId &&
                  <PlaylistProgressProvider>
                    <PlaylistNavigation
                      title={content.title}
                      playlistContextId={playlistContextId}
                      playlistDetail={playlistDetail}
                      playlistContent={playlistContent}
                    />
                  </PlaylistProgressProvider>}
              </Box>
              <VerticalCampaignBanner campaign={campaign} />
              {/* Content Right Side Bar Ad - Only Displayed on Articles */}
              {(useAdsense && shouldDisplayContentStaticAds(content, STATIC_AD_SLOTS.RIGHT_SIDE_BAR)) && (
                <Box mb={2}>
                  <GoogleAd
                    adKey='content'
                    adPosition='right-side-bar'
                    adSource='components>article-view>index.js'
                    isSidePanelAd
                  />
                </Box>
              )}
              <NewsletterCTA />
            </Box>
          </div>
        </>
      )}
    </>
  )
}

export default ArticleView
